/**
 * useDnmSourceAvailability — Phase 2 voting hook
 *
 * Reads wallet DNM balance, approval allowance, staked DNM total,
 * and already-committed staked vote count in a single multicall.
 * Drives the source picker labels and "حداکثر DNM" max calculation.
 *
 * AC-07, AC-10 — source picker label data
 * EC-14, AC-34 — isError propagation for fallback chip values "—"
 * EC-15 — availableStaked clamped >= 0n (defensive against race condition)
 *
 * staleTime intentionally omitted — requires per-call PM approval per 2026-05-05 rule.
 * IDocs spec values (10_000) preserved in service-logic.md if PM re-approves.
 *
 * See: arandao-docs/pipeline/DAO-FOUND-02/idocs/voting/service-logic.md §2.1
 */
import type { Address } from "viem";
import { useReadContracts } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import { ERC20_ABI } from "@/contract/abi/erc20";
import ContractAddress from "@/contract/addresses";

export interface DnmSourceAvailability {
  walletDnm: bigint;
  walletAllowance: bigint;
  stakedDnmTotal: bigint;
  voterStakeCommitted: bigint;
  availableStaked: bigint; // stakedDnmTotal - voterStakeCommitted, clamped >= 0n
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const ZERO_STATE: DnmSourceAvailability = {
  walletDnm: 0n,
  walletAllowance: 0n,
  stakedDnmTotal: 0n,
  voterStakeCommitted: 0n,
  availableStaked: 0n,
  isLoading: false,
  isError: false,
  refetch: () => undefined,
};

export function useDnmSourceAvailability(
  userAddress: Address | undefined,
  cycle: number | undefined,
): DnmSourceAvailability {
  const enabled = userAddress !== undefined && cycle !== undefined;

  const { data, isLoading, isError, refetch } = useReadContracts({
    contracts: [
      {
        address: ContractAddress.DNM,
        abi: ERC20_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "balanceOf",
        args: [userAddress ?? "0x0000000000000000000000000000000000000000"],
      },
      {
        address: ContractAddress.DNM,
        abi: ERC20_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "allowance",
        args: [
          userAddress ?? "0x0000000000000000000000000000000000000000",
          ContractAddress.ELECTION,
        ],
      },
      {
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "getStakedDnm",
        args: [userAddress ?? "0x0000000000000000000000000000000000000000"],
      },
      {
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "voterStakeCount",
        args: [
          userAddress ?? "0x0000000000000000000000000000000000000000",
          BigInt(cycle ?? 0),
        ],
      },
    ],
    query: {
      enabled,
      retry: false,
    },
  });

  if (!enabled) {
    return ZERO_STATE;
  }

  if (isLoading || !data) {
    return { ...ZERO_STATE, isLoading, isError, refetch };
  }

  if (isError) {
    return { ...ZERO_STATE, isLoading: false, isError: true, refetch };
  }

  const walletDnm =
    data[0]?.status === "success" && typeof data[0].result === "bigint"
      ? data[0].result
      : 0n;

  const walletAllowance =
    data[1]?.status === "success" && typeof data[1].result === "bigint"
      ? data[1].result
      : 0n;

  const stakedDnmTotal =
    data[2]?.status === "success" && typeof data[2].result === "bigint"
      ? data[2].result
      : 0n;

  const voterStakeCommitted =
    data[3]?.status === "success" && typeof data[3].result === "bigint"
      ? data[3].result
      : 0n;

  // EC-15: clamp to 0n — never return negative when committed > total (race condition)
  const availableStaked =
    stakedDnmTotal > voterStakeCommitted
      ? stakedDnmTotal - voterStakeCommitted
      : 0n;

  return {
    walletDnm,
    walletAllowance,
    stakedDnmTotal,
    voterStakeCommitted,
    availableStaked,
    isLoading: false,
    isError: false,
    refetch,
  };
}
